export const DEFAULT_PUBLIC_QUERY_TTL_MS = 60 * 1000;
export const DEFAULT_PUBLIC_QUERY_MAX_STALE_MS = 24 * 60 * 60 * 1000;

const completedQueries = new Map();
const inFlightQueries = new Map();

export const readPublicQueryCacheEntry = (
  key,
  {
    ttlMs = DEFAULT_PUBLIC_QUERY_TTL_MS,
    maxStaleMs = DEFAULT_PUBLIC_QUERY_MAX_STALE_MS,
    allowStale = false,
    now = Date.now(),
  } = {},
) => {
  const normalizedKey = String(key || "");
  const entry = completedQueries.get(normalizedKey);
  if (!entry) return null;
  const ageMs = Math.max(0, now - entry.updatedAt);
  if (ageMs > Math.max(ttlMs, maxStaleMs)) {
    completedQueries.delete(normalizedKey);
    return null;
  }
  const stale = ageMs > ttlMs;
  if (stale && !allowStale) return null;
  return {
    value: entry.value,
    updatedAt: entry.updatedAt,
    ageMs,
    stale,
  };
};

export const readPublicQueryCache = (key, options = {}) => (
  readPublicQueryCacheEntry(key, options)?.value ?? null
);

const loadAndRetainPublicQuery = ({
  key,
  load,
  staleEntry,
  retainStaleOnError,
}) => {
  let loaded;
  try {
    loaded = load();
  } catch (error) {
    return staleEntry && retainStaleOnError
      ? Promise.resolve(staleEntry.value)
      : Promise.reject(error);
  }

  let request;
  request = Promise.resolve(loaded)
    .then((value) => {
      completedQueries.set(key, { value, updatedAt: Date.now() });
      return value;
    })
    .catch((error) => {
      if (staleEntry && retainStaleOnError) return staleEntry.value;
      throw error;
    })
    .finally(() => {
      if (inFlightQueries.get(key) === request) inFlightQueries.delete(key);
    });
  inFlightQueries.set(key, request);
  return request;
};

export const fetchPublicQuery = ({
  key,
  load,
  ttlMs = DEFAULT_PUBLIC_QUERY_TTL_MS,
  maxStaleMs = DEFAULT_PUBLIC_QUERY_MAX_STALE_MS,
  force = false,
  staleWhileRevalidate = true,
  retainStaleOnError = true,
}) => {
  const normalizedKey = String(key || "");
  const freshEntry = readPublicQueryCacheEntry(normalizedKey, { ttlMs, maxStaleMs });
  if (!force && freshEntry) return Promise.resolve(freshEntry.value);

  const staleEntry = readPublicQueryCacheEntry(normalizedKey, {
    ttlMs,
    maxStaleMs,
    allowStale: true,
  });
  const activeRequest = inFlightQueries.get(normalizedKey);
  if (activeRequest && !force) {
    if (staleEntry && staleWhileRevalidate) return Promise.resolve(staleEntry.value);
    return activeRequest;
  }

  const request = loadAndRetainPublicQuery({
    key: normalizedKey,
    load,
    staleEntry,
    retainStaleOnError,
  });
  if (!force && staleEntry && staleWhileRevalidate) {
    // The stale value is immediately usable; the registered request safely
    // refreshes it in the background and retains stale data on failure.
    return Promise.resolve(staleEntry.value);
  }
  return request;
};

export const __resetPublicQueryCacheForTests = () => {
  completedQueries.clear();
  inFlightQueries.clear();
};
